import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, CircularProgress, Modal, Portal, useMediaQuery, useTheme } from '@mui/material';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DownloadIcon from '@mui/icons-material/Download';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import KeyboardDoubleArrowDownRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowDownRounded';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AnalyticsIcon from '@mui/icons-material/Analytics';

import { TooltipedActionButton, TooltipedDropdownButton } from 'src/App/Dashboards/Market/Components/Buttons';
import { User } from 'src/interfaces/iuser';
import { callApi } from 'src/core/callApi';
import ComingSoonModal from 'src/App/Dashboards/Market/Modals/ComingSoonModal';
import { useHistory } from 'react-router-dom';
import { filterExampleSavedMarkets, handleSaveDeleteAdvancedMarket } from 'src/utils/functions';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { updateMarketInList } from 'src/store/Dashboard/actions';
import ConfirmationModal from 'src/App/Dashboards/Market/Modals/ConfirmationModal';
import { COLOR_PRIMARY_2 } from 'src/utils/colors';
import { USER_WISE_SAVE_LIST_LIMIT } from 'src/utils/const';
import SavedMarketListsDropdown from 'src/App/Dashboards/Market/Components/Filters/SavedMarketListPage/SavedMarketListsDropdown';

const ActionToolsBar = ({
  setShowListingsExportPage,
  handleOnSampleClick,
  setShowSubmarketCreationModal,
  marketID,
  marketName,
  setUserRemovedThisMarketFromOverview,
  isLoggedIn,
  setShowExportAsPDFModal,
  isPDFDataToDownloadLoading,
  handleClose,
  showExportDashboardOption
}) => {
  const user: User = useSelector((state: any) => state.dashboard.user);
  const search = useSelector((state: any) => state.dashboard.search);
  const savedMarketsState = useSelector((state: any) => state.dashboard.savedMarkets);
  const { data: selectedList } = savedMarketsState;

  const [subMarketOptionOpen, setSubMarketOptionOpen] = useState<boolean>(false);
  const [confirmDeleteMarket, setConfirmDeleteMarket] = useState<{
    marketName: string;
    handleDelete: () => void;
  } | null>(null);

  const [isSelectListModalSelected, setIsSelectListModalSelected] = useState<boolean>(false);

  const [currentSelectedMarketObject, setCurrentSelectedMarketObject] = useState<{
    name: string;
    id: string;
    action: 'add' | 'remove';
    url: string;
  } | null>(null);

  const history = useHistory();
  const dispatch = useDispatch();

  const isMarketInCurrentSavedList =
    selectedList && selectedList.marketDetails && selectedList.marketIDs[marketID];

  useEffect(() => {
    if (selectedList) {
      setCurrentSelectedMarketObject({
        name: marketName,
        id: marketID,
        action: isMarketInCurrentSavedList ? 'remove' : 'add',
        url: '',
      });
    }
  }, [selectedList]);

  const marketList = useMemo(() => {
    let markets = Object.values(savedMarketsState.data?.marketDetails || {})
      .filter(market => market !== null)
      .map((market: any) => ({
        ...market,
        id: market.market_id,
      }));

    return markets;
  }, [savedMarketsState]);

  const handleSaveClick = async () => {
    if (!isLoggedIn) {
      handleOnSampleClick('expert');
      return;
    }

    if (isMarketInCurrentSavedList) {
      setConfirmDeleteMarket({
        marketName: marketName,
        handleDelete: () => {
          dispatch(updateMarketInList(selectedList, Number(marketID), 'remove', ''));
        },
      });
    } else {
      if (
        user.subscriptionPlan === 'starter' &&
        filterExampleSavedMarkets(marketList, true).length >=
          USER_WISE_SAVE_LIST_LIMIT[user.subscriptionPlan]
      ) {
        handleOnSampleClick('expert');
        return;
      }

      setIsSelectListModalSelected(true);
    }
  };

  const handleDownloadMarketSummarySampleClick = () => {
    logAmplitudeEvent('market_summary_sample_downloaded');
    const downloadUrl =
      'https://airbtics-resource.s3.us-east-2.amazonaws.com/Airbtics-Sample-Market-Summary.csv';
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', 'Airbtics-Sample-Market-Summary.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubscribeToScaleClick = () => {
    handleOnSampleClick('scale');
  };

  const handleDownloadListingLevelClick = () => {
    logAmplitudeEvent('listing_export_modal_opened');
    setShowListingsExportPage(true);
  };

  const handleExportAsPDFClick = () => {
    setShowExportAsPDFModal(true);
  };

  const downloadOptions = [
    { label: 'Download Sample', onClick: handleDownloadMarketSummarySampleClick },
    { label: 'Subscribe to Scale', onClick: handleSubscribeToScaleClick },
  ];

  const getIfMarketSavedStatusIcon = () => {
    if (isMarketInCurrentSavedList === 'saving') return AutorenewIcon;

    if (isMarketInCurrentSavedList) return BookmarkIcon;

    return BookmarkAddOutlinedIcon;
  };

  const getIfMarketSavedStatusText = () => {
    if (isMarketInCurrentSavedList === 'saving') return 'Saving';

    if (isMarketInCurrentSavedList) return 'Saved';

    return 'Save';
  };

  const isSubmarket = search.data.isSubmarket;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.up('xs'));

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
          backgroundColor: '#f5f5f5',
          borderRadius: '12px',
          padding: '10px 20px',
          width: 'fit-content',
          margin: '0 auto',
          gap: '10px',
        }}
      >
        <TooltipedActionButton
          title={isMarketInCurrentSavedList ? 'Remove from Saved Markets' : 'Add to Saved Markets'}
          onClick={handleSaveClick}
          icon={getIfMarketSavedStatusIcon()}
          label={getIfMarketSavedStatusText()}
          animation={isMarketInCurrentSavedList === 'saving' ? 'animation-loading-spin' : undefined}
        />

        <TooltipedActionButton
          title="Save markets to track key metrics and export to Excel. Only available on Scale plan."
          onClick={() => history.push('/airbnb-data/landing')}
          icon={AnalyticsIcon}
          label={'My Markets'}
          smallLabel={true}
        />

        {/* {user.subscriptionPlan != 'scale' ? (
          <TooltipedDropdownButton
            title="Available on the Scale plan only. If you're on the Scale plan, save the market first and click here. This will open the advanced dashboard, where you can download the market summary for all your saved markets."
            icon={DownloadIcon}
            label="Market Summary"
            options={downloadOptions}
            smallLabel={true}
          />
        ) : (
          <TooltipedActionButton
            title="Available on the Scale plan only. If you're on the Scale plan, save the market first and click here. This will open the advanced dashboard, where you can download the market summary for all your saved markets."
            onClick={() => history.push('/advanced-dashboard')} // TODO: implement this
            icon={DownloadIcon}
            label="Market Summary"
            smallLabel={true}
          />
        )} */}

        <TooltipedActionButton
          title="Download all the data on short-term rental listings in this market. This is an add-on product. It is not included in any of the subscription plans. Purchase it now."
          onClick={handleDownloadListingLevelClick}
          icon={DownloadIcon}
          label="Listing Level"
          smallLabel={true}
        />

        {!isSubmarket && (
          <TooltipedActionButton
            title=""
            onClick={() => setSubMarketOptionOpen(true)}
            icon={KeyboardDoubleArrowDownRoundedIcon}
            label="My Submarkets"
            smallLabel={true}
          />
        )}

        {showExportDashboardOption && (
          <TooltipedActionButton
            title={
              isPDFDataToDownloadLoading
                ? '(Loading...) Export the current market data as PDF'
                : 'Export the current market data as PDF'
            }
            onClick={
              (!isPDFDataToDownloadLoading || user.subscriptionPlan === 'starter')
                ? () => {
                    handleExportAsPDFClick();
                    handleClose();
                  }
                : () => {}
            }
            icon={(isPDFDataToDownloadLoading && user.subscriptionPlan !== 'starter') ? AutorenewIcon : SaveAltIcon}
            label="Export as PDF"
            smallLabel={true}
            highlight={true}
            animation={(isPDFDataToDownloadLoading && user.subscriptionPlan !== 'starter') ? 'animation-loading-spin' : undefined}
          />
        )}
      </Box>
      {subMarketOptionOpen && <ComingSoonModal open={true} setOpen={setSubMarketOptionOpen} />}

      {confirmDeleteMarket && (
        <ConfirmationModal
          open={confirmDeleteMarket !== null}
          onClose={() => setConfirmDeleteMarket(null)}
          title={
            <>
              Are you sure you want to remove{' '}
              <span style={{ color: COLOR_PRIMARY_2 }}>{confirmDeleteMarket.marketName}</span> from
              your Saved Markets list?
            </>
          }
          confirmButtonText="Yes"
          onConfirm={() => {
            setUserRemovedThisMarketFromOverview(true);
            confirmDeleteMarket.handleDelete();
          }}
          onCancel={() => setConfirmDeleteMarket(null)}
          cancelButtonText="No"
        />
      )}

      {isSelectListModalSelected && (
        <Portal container={document.querySelector('body')}>
          <Modal open={true} onClose={() => setIsSelectListModalSelected(false)}>
            <Box
              sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: 'background.paper',
                border: 'none',
                outline: 'none',
                boxShadow: 24,
                p: '10px',
                borderRadius: '12px',
                width: '90vw',
                maxWidth: '400px',
              }}
            >
              <SavedMarketListsDropdown
                mode="saveListinExplorerPage"
                currentSelectedMarketObject={currentSelectedMarketObject}
                handleOnSampleClick={handleOnSampleClick}
                handleOutSideClose={() => setIsSelectListModalSelected(false)}
              />
            </Box>
          </Modal>
        </Portal>
      )}
    </>
  );
};

export default ActionToolsBar;
